import { clearAssetsSelection } from 'actions/layer'
import { checkIntersectRect } from 'helpers/checkIntersectRect'
import { useAction } from 'hooks/utils'
import { isEmpty, isEqual } from 'lodash'
import { useCallback, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { getLayers } from '~/selectors'
import { selectPreviewVideoSize } from '~/selectors/sourceFiles'
import * as ActionTypes from '~/actions/ActionTypes'
import { LAYER_ITEM_CONTAINER_CLASSNAME } from '~/config/constants/layer'
import { isRecordingSaving, isRecordingStarted } from '~/selectors/recording'
import { useScrollBarControl } from './useScrollBarControl'

export const useAllocationAria = (layersRef, scrollbarRef, scale) => {
  const allocationAriaRef = useRef()
  const isMouseDown = useRef(false)
  const allocationAriaStartCoords = useRef({ startX: 0, startY: 0 })
  const intersectedAssetIds = useRef([])

  const dispatch = useDispatch()
  const previewSize = useSelector(selectPreviewVideoSize)
  const mediaRecording = useSelector(isRecordingStarted)
  const isSaving = useSelector(isRecordingSaving)
  const clearSelection = useAction(clearAssetsSelection)
  const allLayers = useSelector(getLayers)
  const {
    onScrollTimelineY,
    onScrollTimelineX,
    scrollTopInterval,
    scrollLeftInterval,
  } = useScrollBarControl(scrollbarRef, scale)
  const disabledCtrls = isSaving || mediaRecording
  const onSelectElements = useCallback(intersectedAssetIds => {
    if (!isEmpty(intersectedAssetIds)) {
      dispatch({
        type: ActionTypes.TOGGLE_SELECTED_ASSETS,
        payload: { assetsIds: intersectedAssetIds },
      })
    } else {
      clearSelection()
    }
  }, [ dispatch, clearSelection ])

  const getIntersectedAssets = useCallback((assets, allocationAriaElement) => {
    const intersectedAssetIds = new Set()
    assets.forEach(asset => {
      const assetId = asset.getAttribute('data-id')
      if (checkIntersectRect(asset.getBoundingClientRect(),
        allocationAriaElement.getBoundingClientRect())) {
        intersectedAssetIds.add(assetId)
      }
    })
    return Array.from(intersectedAssetIds)
  }, [])

  const createAllocationAria = useCallback((event, containerX, containerY, scrollHeight) => {
    const scrollTop = scrollbarRef.current.getScrollTop()
    const scrollLeft = scrollbarRef.current.getScrollLeft()
    const mouseX = event.clientX + scrollLeft
    const mouseY = event.clientY + scrollTop
    const { startX, startY } = allocationAriaStartCoords.current

    const left = startX > mouseX ? mouseX - containerX : startX - containerX
    const right = startX > mouseX
      ? startX - containerX : mouseX - containerX
    allocationAriaRef.current.style.left = `${left}px`
    allocationAriaRef.current.style.right = `${right}px`
    allocationAriaRef.current.style.width = `${right - left}px`

    const top = startY > mouseY
      ? mouseY - containerY : startY - containerY
    const bottom = startY > mouseY
      ? startY - containerY : mouseY - containerY
    const height = bottom - top
    allocationAriaRef.current.style.top = `${top}px`
    allocationAriaRef.current.style.bottom = `${bottom}px`
    allocationAriaRef.current.style.height = `${height > scrollHeight ? scrollHeight : height}px`
  }, [ scrollbarRef ])

  useEffect(() => {
    isMouseDown.current = false
    const scrollBarLayersContaner = scrollbarRef.current.container
    const allAssets = document.getElementsByClassName(LAYER_ITEM_CONTAINER_CLASSNAME)
    const scrollHeight = scrollbarRef.current.getScrollHeight()

    function onMouseDown(event) {
      isMouseDown.current = false
      allocationAriaRef.current.style.display = 'none'
      window.getSelection().removeAllRanges()
      if ([ 'layer', 'layer__items', 'layer__control', 'scroll-container__content' ]
        .includes(event.target.className) && !disabledCtrls) {
        isMouseDown.current = true
        allocationAriaStartCoords.current.startX = event.clientX
        + scrollbarRef.current.getScrollLeft()
        allocationAriaStartCoords.current.startY = event.clientY
        + scrollbarRef.current.getScrollTop()
        allocationAriaRef.current.style.display = 'block'
      }
    }

    function onMouseUp() {
      if (isMouseDown.current) {
        allocationAriaRef.current.style.width = 0
        allocationAriaRef.current.style.height = 0
        allocationAriaRef.current.style.left = 0
        allocationAriaRef.current.style.top = 0
        allocationAriaRef.current.style.display = 'none'
        isMouseDown.current = false
        window.getSelection().removeAllRanges()
        intersectedAssetIds.current = []
      }
      clearInterval(scrollTopInterval.current)
      scrollTopInterval.current = false
      clearInterval(scrollLeftInterval.current)
      scrollLeftInterval.current = false
    }

    function onMouseMove(event) {
      const {
        x: containerX,
        y: containerY,
      } = scrollBarLayersContaner.getBoundingClientRect()
      if (isMouseDown.current && allocationAriaRef.current) {
        onScrollTimelineY(event)
        onScrollTimelineX(event)
        window.getSelection().removeAllRanges()
        createAllocationAria(event, containerX, containerY, scrollHeight)
        const newIntersectedAssetIds = getIntersectedAssets(Array.from(allAssets),
          allocationAriaRef.current, event.target.dataset.id)
        if (!isEqual(newIntersectedAssetIds, intersectedAssetIds.current)) {
          onSelectElements(newIntersectedAssetIds)
          intersectedAssetIds.current = newIntersectedAssetIds
        }
      }
    }

    scrollBarLayersContaner.addEventListener('mousedown', onMouseDown)
    document.addEventListener('mouseup', onMouseUp)
    document.addEventListener('mousemove', onMouseMove)
    return () => {
      scrollBarLayersContaner.removeEventListener('mousedown', onMouseDown)
      document.removeEventListener('mouseup', onMouseUp)
      document.removeEventListener('mousemove', onMouseMove)
    }
  }, [
    getIntersectedAssets,
    createAllocationAria,
    onSelectElements,
    previewSize,
    layersRef,
    dispatch,
    scrollbarRef,
    allLayers,
    onScrollTimelineY,
    onScrollTimelineX,
    scrollTopInterval,
    scrollLeftInterval,
    disabledCtrls,
  ])

  return { allocationAriaRef }
}
